import React, { useEffect, useState, useContext } from "react";
import { Drawer, Form, Input, Select, Button, Space, Row, Col, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import FyiImportService from "../../Services/FyiImportService";
import { adminContext } from "../../Common/PageRoute";
import { IPractices, usePractice } from "../../Context/PracticeContext";

const { Option } = Select;
const { Text } = Typography;

export interface FyiImportFormProps {
  visible: boolean;
  onClose: (confirmed: boolean) => void;
  initialValues?: any;
  isEdit?: boolean;
}

const AWS_REGIONS = [
  { label: "US East (N. Virginia) - us-east-1", value: "us-east-1" },
  { label: "US East (Ohio) - us-east-2", value: "us-east-2" },
  { label: "US West (N. California) - us-west-1", value: "us-west-1" },
  { label: "US West (Oregon) - us-west-2", value: "us-west-2" },
  { label: "Europe (Ireland) - eu-west-1", value: "eu-west-1" },
  { label: "Europe (London) - eu-west-2", value: "eu-west-2" },
  { label: "Europe (Frankfurt) - eu-central-1", value: "eu-central-1" },
  { label: "Asia Pacific (Singapore) - ap-southeast-1", value: "ap-southeast-1" },
  { label: "Asia Pacific (Sydney) - ap-southeast-2", value: "ap-southeast-2" },
  { label: "Asia Pacific (Tokyo) - ap-northeast-1", value: "ap-northeast-1" },
  { label: "Canada (Central) - ca-central-1", value: "ca-central-1" },
];

const FyiImportForm: React.FC<FyiImportFormProps> = ({ visible, onClose, initialValues, isEdit }) => {
  const [form] = Form.useForm();
  const { openNotification } = useContext(adminContext);
  const [loading, setLoading] = useState(false);
  const { allPracticeIds } = usePractice();

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        form.setFieldsValue({
          practiceId: initialValues.practiceId,
          bucketName: initialValues.bucketName,
          region: initialValues.region,
          awsAccessKeyId: initialValues.awsAccessKeyId === "****" ? "" : initialValues.awsAccessKeyId,
          awsSecretAccessKey: initialValues.awsSecretAccessKey === "****" ? "" : initialValues.awsSecretAccessKey,
          maxConcurrency: initialValues.maxConcurrency || 8,
          targetClientId: initialValues.targetClientId || "",
          s3Prefix: initialValues.s3Prefix || "",
        });
      } else {
        form.resetFields();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initialValues]);

  const handleSave = () => {
    form.validateFields()
      .then((values) => {
        setLoading(true);
        const action = isEdit && initialValues?.id
          ? FyiImportService.update(initialValues.id, values)
          : FyiImportService.create(values);

        action
          .then((res) => {
            setLoading(false);
            if (res?.status) {
              openNotification(
                "success",
                isEdit ? "FYI Import Job updated successfully" : "FYI Import Job enqueued successfully"
              );
              onClose(true);
            } else {
              openNotification("error", res?.message || `Failed to ${isEdit ? "update" : "enqueue"} job`);
            }
          })
          .catch((err) => {
            setLoading(false);
            openNotification("error", err?.response?.data?.message || `Error ${isEdit ? "updating" : "enqueuing"} job`);
          });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleClose = () => {
    form.resetFields();
    onClose(false);
  };

  return (
    <Drawer
      title={isEdit ? "Edit FYI Import Job" : (initialValues ? "Clone Import Job" : "New FYI Import Job")}
      placement="right"
      width={600}
      onClose={handleClose}
      open={visible}
      destroyOnClose
      closable={true}
      extra={<Button type="text" icon={<CloseOutlined />} onClick={handleClose} />}
      footer={
        <div style={{ textAlign: "right" }}>
          <Space>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSave}
              loading={loading}
              // disabled={fetchingPractices}
              style={{ borderRadius: 6 }}
            >
              {isEdit ? "Save Changes" : "Enqueue Job"}
            </Button>
          </Space>
        </div>
      }
    >
      <div style={{ padding: "16px 24px" }}>
        <Form form={form} layout="vertical" requiredMark={true}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Practice"
                name="practiceId"
                rules={[{ required: true, message: "Select a Practice" }]}
                style={{ marginBottom: 20 }}
              >
                <Select
                  showSearch
                  placeholder="Search and select a Practice"
                  filterOption={(input: any, option: any) => {
                    return option?.label
                      ?.toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                  style={{ borderRadius: 6 }}
                  options={allPracticeIds.map((p: IPractices) => ({
                    value: p.value,
                    label: p.label + " (ID: " + p.value + ")",
                  }))}
                />
                {/* {practices.map((p) => (
                      <Option key={p.id} value={p.id}>
                        {p.name} (ID: {p.id})
                      </Option>
                    ))}
                  </Select> */}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="AWS S3 Bucket Name"
                name="bucketName"
                rules={[{ required: true, message: "Enter AWS S3 Bucket Name" }]}
                style={{ marginBottom: 20 }}
              >
                <Input placeholder="Enter bucket name" style={{ borderRadius: 6 }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="AWS Region"
                name="region"
                rules={[{ required: true, message: "Select AWS Region" }]}
                style={{ marginBottom: 20 }}
              >
                <Select placeholder="Select AWS Region" style={{ borderRadius: 6 }}>
                  {AWS_REGIONS.map((r) => (
                    <Option key={r.value} value={r.value}>
                      {r.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="AWS S3 Prefix (Sub-folder Path)"
                name="s3Prefix"
                style={{ marginBottom: 20 }}
                help="Optional. If files are stored inside a folder (e.g. folder/subfolder), enter it here. Do not start with a slash."
              >
                <Input placeholder="e.g. 30-06-2026, 17-01-46/export-data" style={{ borderRadius: 6 }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="AWS Access Key ID"
                name="awsAccessKeyId"
                rules={[{ required: !isEdit, message: "Enter AWS Access Key ID" }]}
                style={{ marginBottom: 20 }}
              >
                <Input placeholder={isEdit ? "Leave empty to keep existing key" : "Enter AWS Access Key ID"} style={{ borderRadius: 6 }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="AWS Secret Access Key"
                name="awsSecretAccessKey"
                rules={[{ required: !isEdit, message: "Enter AWS Secret Access Key" }]}
                style={{ marginBottom: 20 }}
              >
                <Input.Password placeholder={isEdit ? "Leave empty to keep existing key" : "Enter AWS Secret Access Key"} style={{ borderRadius: 6 }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Max Concurrency (Parallel S3 Streams)"
                name="maxConcurrency"
                initialValue={8}
                style={{ marginBottom: 20 }}
              >
                <Select placeholder="Select Concurrency" style={{ borderRadius: 6 }}>
                  <Option value={1}>1 (Sequential - Slowest/Safest)</Option>
                  <Option value={2}>2 Parallel Streams</Option>
                  <Option value={4}>4 Parallel Streams</Option>
                  <Option value={8}>8 Parallel Streams (Recommended)</Option>
                  <Option value={12}>12 Parallel Streams</Option>
                  <Option value={16}>16 Parallel Streams (High Load/Fastest)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Target Client ID (Dry Run Filter)"
                name="targetClientId"
                style={{ marginBottom: 20 }}
                help="Optional. If set, the importer will only process records for this specific Client ID to prevent resource load."
              >
                <Input placeholder="e.g. CA-179" style={{ borderRadius: 6 }} />
              </Form.Item>
            </Col>
          </Row>

          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: "10px" }}>
            * Make sure the IAM User has read permissions to the specified AWS S3 bucket.
          </Text>
        </Form>
      </div>
    </Drawer>
  );
};

export default FyiImportForm;
